import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  Loader2,
  ShieldCheck,
  CheckCircle2,
  MapPin,
  Calendar as CalendarIcon,
  Clock,
  AlertTriangle,
  CircleDot,
  ArrowRight,
  LogIn,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import WaiverSuccess from "@/components/site/WaiverSuccess";
import CheckInEmailPrompt from "@/components/site/CheckInEmailPrompt";
import SignaturePad from "@/components/site/SignaturePad";
import DateOfBirthPicker from "@/components/site/DateOfBirthPicker";
import PhoneInput, { formatPhoneForApi } from "@/components/site/PhoneInput";
import { DEFAULT_PHONE_COUNTRY, isValidPhoneForApi } from "@/lib/phone";

const STEP = { LOADING: 0, SIGN_IN: 1, GUEST_INFO: 7, WAIVER: 2, CONFIRM: 3, DONE: 4, ERROR: 5, WAIVER_DONE: 6, READY: 8 };

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const buildGoogleAuthUrl = (clientId, returnPath) => {
  const redirectUri = window.location.origin + "/auth/google";
  const params = new URLSearchParams({
    client_id: clientId,
    redirect_uri: redirectUri,
    response_type: "code",
    scope: "openid email profile",
    access_type: "online",
    prompt: "select_account",
    state: encodeURIComponent(returnPath || "/"),
  });
  return `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
};

const InfoRow = ({ icon: Icon, label, value }) => (
  <li className="flex items-start gap-3 font-exo text-sm">
    <Icon className="w-4 h-4 text-spike-blue-light mt-0.5 shrink-0" strokeWidth={1.5} />
    <div>
      <div className="font-orbitron text-[0.6rem] uppercase tracking-[0.25em] text-spike-blue-light">
        {label}
      </div>
      <div className="mt-0.5 text-spike-text">{value}</div>
    </div>
  </li>
);

const CheckIn = () => {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading: authLoading, refresh } = useAuth();

  const [step, setStep] = useState(STEP.LOADING);
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [waiver, setWaiver] = useState(null);
  const [waiverStatus, setWaiverStatus] = useState(null);
  const [signatureName, setSignatureName] = useState("");
  const [signatureType, setSignatureType] = useState("typed"); // "typed" | "drawn"
  const [signatureDrawnPng, setSignatureDrawnPng] = useState("");
  const [dob, setDob] = useState(""); // DD/MM/YYYY
  const [guardianName, setGuardianName] = useState("");
  const [guardianEmail, setGuardianEmail] = useState("");
  const [guardianPhoneDial, setGuardianPhoneDial] = useState(DEFAULT_PHONE_COUNTRY.dial);
  const [guardianPhoneNational, setGuardianPhoneNational] = useState("");
  const [guardianRelationship, setGuardianRelationship] = useState("");
  const [typedSignIntent, setTypedSignIntent] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [myCheckIn, setMyCheckIn] = useState(null);
  const [googleClientId, setGoogleClientId] = useState("");
  const [signedMeta, setSignedMeta] = useState(null); // { signedAt, signatureName, parentGuardianName }
  const [guestEmailSent, setGuestEmailSent] = useState("");
  const [guestFullName, setGuestFullName] = useState("");
  const [guestEmail, setGuestEmail] = useState("");
  const [guestPhoneDial, setGuestPhoneDial] = useState(DEFAULT_PHONE_COUNTRY.dial);
  const [guestPhoneNational, setGuestPhoneNational] = useState("");

  const isStandalone = !bookingId;
  const guestMode = new URLSearchParams(location.search).get("guest") === "1";

  // 1. Load public booking info (if bookingId) + waiver text + google config
  useEffect(() => {
    (async () => {
      try {
        const calls = [api.get("/waiver/current"), api.get("/auth/google/config")];
        if (bookingId) calls.unshift(api.get(`/bookings/${bookingId}/checkin-info`));
        const results = await Promise.all(calls);
        if (bookingId) {
          const [b, w, g] = results;
          setInfo(b.data);
          setWaiver(w.data);
          setGoogleClientId(g.data.enabled ? g.data.client_id : "");
          if (b.data.status === "cancelled") {
            setError("This booking has been cancelled.");
            setStep(STEP.ERROR);
          }
        } else {
          const [w, g] = results;
          setWaiver(w.data);
          setGoogleClientId(g.data.enabled ? g.data.client_id : "");
          setInfo({ standalone: true }); // sentinel so advance() runs
        }
      } catch (e) {
        setError(e?.response?.data?.detail || "This check-in link is invalid or expired.");
        setStep(STEP.ERROR);
      }
    })();
  }, [bookingId]);

  // Drop the booking listing call for standalone — just fetch waiver status
  const advance = useCallback(async () => {
    if (!info || authLoading || step === STEP.ERROR) return;

    if (isStandalone && guestMode) {
      if (step === STEP.LOADING || step === STEP.SIGN_IN) {
        setStep(STEP.GUEST_INFO);
      }
      return;
    }

    if (!user) {
      setStep(STEP.SIGN_IN);
      return;
    }
    try {
      const calls = [api.get("/me/waiver-status", { withCredentials: true })];
      if (bookingId) calls.push(api.get(`/bookings/${bookingId}/my-checkin`, { withCredentials: true }));

      const [w, second] = await Promise.all(calls);
      setWaiverStatus(w.data);

      if (bookingId) {
        if (second.data.checked_in) {
          setMyCheckIn(second.data);
          setStep(STEP.DONE);
          return;
        }
        if (!w.data.signed || w.data.needs_resign) {
          setSignatureName(user.name || "");
          setStep(STEP.WAIVER);
        } else {
          setStep(STEP.CONFIRM);
        }
      } else {
        if (!w.data.signed || w.data.needs_resign) {
          setSignatureName(user.name || "");
          setStep(STEP.WAIVER);
        } else {
          setStep(STEP.READY);
        }
      }
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not load your account info.");
      setStep(STEP.ERROR);
    }
  }, [info, authLoading, user, bookingId, step, guestMode, isStandalone]);

  useEffect(() => {
    advance();
  }, [advance]);

  // Compute age from DOB (DD/MM/YYYY) for live "minor" detection
  const ageYears = React.useMemo(() => {
    const m = dob.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (!m) return null;
    const d = new Date(parseInt(m[3], 10), parseInt(m[2], 10) - 1, parseInt(m[1], 10));
    if (Number.isNaN(d.getTime())) return null;
    const now = new Date();
    let age = now.getFullYear() - d.getFullYear();
    const md = now.getMonth() - d.getMonth();
    if (md < 0 || (md === 0 && now.getDate() < d.getDate())) age -= 1;
    return age;
  }, [dob]);
  const isMinor = ageYears !== null && ageYears < 18 && ageYears >= 0;

  const signWaiver = async () => {
    if (!agreed) {
      toast.error("Please tick the box to agree.");
      return;
    }
    if (signatureType === "typed") {
      if (!typedSignIntent) {
        toast.error("Please check the box confirming your intent to legally sign this waiver.");
        return;
      }
      if (signatureName.trim().length < 2) {
        toast.error("Please type your full name as signature.");
        return;
      }
    } else if (signatureType === "drawn") {
      if (!signatureDrawnPng) {
        toast.error("Please draw your signature in the box.");
        return;
      }
      if (signatureName.trim().length < 2) {
        toast.error("Please also type your name for the record.");
        return;
      }
    }
    if (!/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(dob)) {
      toast.error("Date of birth must be in DD/MM/YYYY format.");
      return;
    }
    if (ageYears === null || ageYears < 5 || ageYears > 120) {
      toast.error("Please enter a valid date of birth.");
      return;
    }
    if (isMinor) {
      if (guardianName.trim().length < 2) {
        toast.error("Parent or guardian name is required for participants under 18.");
        return;
      }
      if (!guardianEmail.trim() || !guardianEmail.includes("@")) {
        toast.error("Parent or guardian email is required for participants under 18.");
        return;
      }
      if (!isValidPhoneForApi(guardianPhoneDial, guardianPhoneNational)) {
        toast.error("Parent or guardian phone is required for participants under 18.");
        return;
      }
      if (guardianRelationship.trim().length < 2) {
        toast.error("Parent or guardian relationship is required for participants under 18.");
        return;
      }
    }
    const guestPhoneFormatted = formatPhoneForApi(guestPhoneDial, guestPhoneNational);
    const guardianPhoneFormatted = isMinor
      ? formatPhoneForApi(guardianPhoneDial, guardianPhoneNational)
      : "";
    setSubmitting(true);
    try {
      if (isStandalone && guestMode) {
        if (!guestFullName.trim() || guestFullName.trim().length < 2) {
          toast.error("Please enter your full name.");
          setSubmitting(false);
          return;
        }
        if (!guestEmail.trim()) {
          toast.error("Please enter your email.");
          setSubmitting(false);
          return;
        }
        if (!isValidPhoneForApi(guestPhoneDial, guestPhoneNational)) {
          toast.error("Please enter a valid phone number.");
          setSubmitting(false);
          return;
        }
        await api.post("/waiver/guest-sign", {
          full_name: guestFullName.trim(),
          email: guestEmail.trim(),
          phone: guestPhoneFormatted,
          signature_name: signatureName,
          agreed: true,
          typed_sign_intent: signatureType === "typed" ? typedSignIntent : false,
          date_of_birth: dob,
          parent_guardian_name: isMinor ? guardianName : null,
          guardian_email: isMinor ? guardianEmail.trim() : null,
          guardian_phone: isMinor ? guardianPhoneFormatted : null,
          guardian_relationship: isMinor ? guardianRelationship.trim() : null,
          signature_type: signatureType,
          signature_drawn_png: signatureType === "drawn" ? signatureDrawnPng : null,
        });
        setGuestEmailSent(guestEmail.trim());
        toast.success("Waiver signed — check your email for your QR.");
        setStep(STEP.WAIVER_DONE);
        return;
      }

      await api.post(
        "/waiver/sign",
        {
          signature_name: signatureName,
          agreed: true,
          typed_sign_intent: signatureType === "typed" ? typedSignIntent : false,
          date_of_birth: dob,
          parent_guardian_name: isMinor ? guardianName : null,
          guardian_email: isMinor ? guardianEmail.trim() : null,
          guardian_phone: isMinor ? guardianPhoneFormatted : null,
          guardian_relationship: isMinor ? guardianRelationship.trim() : null,
          signature_type: signatureType,
          signature_drawn_png: signatureType === "drawn" ? signatureDrawnPng : null,
        },
        { withCredentials: true }
      );
      toast.success("Waiver signed — check your email for your check-in QR.");
      const signedAt = new Date().toISOString();
      setSignedMeta({
        signedAt,
        signatureName: signatureName.trim(),
        parentGuardianName: isMinor ? guardianName.trim() : null,
      });
      setStep(isStandalone ? STEP.WAIVER_DONE : STEP.CONFIRM);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not sign waiver.");
    } finally {
      setSubmitting(false);
    }
  };

  const confirmCheckIn = async () => {
    setSubmitting(true);
    try {
      const endpoint = isStandalone ? "/me/checkin" : `/bookings/${bookingId}/checkin`;
      const { data } = await api.post(endpoint, {}, { withCredentials: true });
      setMyCheckIn(data);
      setStep(STEP.DONE);
      if (!isStandalone) {
        const fresh = await api.get(`/bookings/${bookingId}/checkin-info`);
        setInfo(fresh.data);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not check you in.");
    } finally {
      setSubmitting(false);
    }
  };

  const startGoogleSignIn = () => {
    if (!googleClientId) {
      toast.error("Sign-in is not configured. Please contact the venue.");
      return;
    }
    window.location.href = buildGoogleAuthUrl(googleClientId, location.pathname);
  };

  // ====== Render ======
  if (step === STEP.LOADING || authLoading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center" data-testid="checkin-loading">
        <Loader2 className="w-6 h-6 animate-spin text-spike-blue-light" />
      </div>
    );
  }

  if (step === STEP.ERROR) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-6">
        <div className="max-w-md w-full bg-spike-elevated border border-red-500/30 p-10 text-center" data-testid="checkin-error">
          <AlertTriangle className="w-10 h-10 text-red-400 mx-auto mb-4" />
          <h1 className="font-orbitron font-black uppercase text-2xl text-spike-text">
            Check-in unavailable
          </h1>
          <p className="mt-3 font-exo text-spike-text-muted text-sm">{error}</p>
          <button
            type="button"
            onClick={() => navigate("/")}
            className="mt-6 inline-flex items-center gap-2 bg-spike-blue hover:bg-spike-blue-hover text-white font-orbitron uppercase tracking-[0.22em] text-xs px-6 py-3"
          >
            Back home
          </button>
        </div>
      </div>
    );
  }

  if (step === STEP.WAIVER_DONE) {
    if (guestMode) {
      return (
        <CheckInEmailPrompt
          title="Guest waiver signed"
          guest
          email={guestEmailSent}
          showAccountLink={false}
        />
      );
    }
    if (isStandalone) {
      return (
        <CheckInEmailPrompt
          title="Waiver signed"
          email={user?.email}
        />
      );
    }
    return (
      <WaiverSuccess
        user={user}
        waiverText={waiver?.body}
        signedAt={signedMeta?.signedAt}
        signatureName={signedMeta?.signatureName}
        parentGuardianName={signedMeta?.parentGuardianName}
      />
    );
  }

  if (step === STEP.READY) {
    return (
      <CheckInEmailPrompt
        title="You're cleared to check in"
        email={user?.email}
      />
    );
  }

  return (
    <div className="bg-spike-surface min-h-screen" data-testid="checkin-page">
      <section className="relative pt-14 pb-10 border-b border-white/10 overflow-hidden">
        <div className="absolute inset-0 stripe-pattern opacity-50" />
        <div className="relative max-w-[1000px] mx-auto px-6 lg:px-12">
          <div className="font-orbitron text-[0.7rem] uppercase tracking-[0.4em] text-spike-blue-light flex items-center gap-3">
            <span className="w-8 h-px bg-spike-blue" /> Check-in
          </div>
          <h1 className="mt-5 font-orbitron font-black uppercase text-3xl sm:text-4xl text-spike-text leading-tight">
            {step === STEP.DONE
              ? <>You're <span className="text-spike-blue-light">checked in.</span></>
              : step === STEP.SIGN_IN
              ? <>Sign in to <span className="text-spike-blue-light">check in.</span></>
              : step === STEP.GUEST_INFO
              ? <>Guest <span className="text-spike-blue-light">check-in.</span></>
              : step === STEP.WAIVER
              ? <>One quick <span className="text-spike-blue-light">waiver.</span></>
              : <>Confirm your <span className="text-spike-blue-light">check-in.</span></>}
          </h1>
        </div>
      </section>

      <section className="py-10">
        <div className="max-w-[1000px] mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Booking card */}
          {info && !info.standalone && (
            <aside className="lg:col-span-5">
              <div className="bg-spike-elevated border border-white/10 p-7 sticky top-28" data-testid="checkin-booking-card">
                <div className="font-orbitron text-[0.65rem] uppercase tracking-[0.3em] text-spike-blue-light">
                  Booking · #{info.id.slice(0, 6).toUpperCase()}
                </div>
                <div className="mt-2 font-orbitron font-bold uppercase text-spike-text text-xl tracking-wide capitalize">
                  {info.sport} · {info.lane_type.replace("_", " ")}
                </div>
                <ul className="mt-6 space-y-4">
                  <InfoRow icon={MapPin} label="Venue" value={`${info.venue_name}${info.venue_address ? " — " + info.venue_address : ""}`} />
                  <InfoRow icon={CalendarIcon} label="Date" value={info.date} />
                  <InfoRow icon={Clock} label="Time" value={`${info.time_slot} · ${info.duration_minutes} min`} />
                  {info.machine && <InfoRow icon={CircleDot} label="Machine" value={info.machine.replace("_", " ")} />}
                  <InfoRow
                    icon={CheckCircle2}
                    label="Checked in so far"
                    value={info.party_size ? `${info.checked_in_count} / ${info.party_size}` : `${info.checked_in_count}`}
                  />
                </ul>
              </div>
            </aside>
          )}

          {/* Right column = active step */}
          <div className={info && !info.standalone ? "lg:col-span-7" : "lg:col-span-12"}>
            {step === STEP.SIGN_IN && (
              <div className="bg-spike-elevated border border-white/10 p-8" data-testid="checkin-step-signin">
                <ShieldCheck className="w-8 h-8 text-spike-blue-light mb-5" strokeWidth={1.5} />
                <h2 className="font-orbitron font-bold uppercase text-xl text-spike-text tracking-wider">
                  Sign in to continue
                </h2>
                <p className="mt-3 font-exo text-spike-text-muted text-sm">
                  We need your name &amp; email so we can record your check-in and link your waiver to your account.
                  Sign in with email + password, or with your Google account.
                </p>
                <button
                  type="button"
                  data-testid="checkin-signin-btn"
                  onClick={() => navigate("/login", { state: { next: location.pathname + location.search } })}
                  className="mt-6 inline-flex items-center justify-center gap-3 bg-spike-blue hover:bg-spike-blue-hover text-black font-orbitron font-bold uppercase tracking-[0.2em] text-xs px-6 py-4 transition-colors"
                >
                  Sign In / Sign Up
                </button>
                <p className="mt-6 text-[0.65rem] font-exo text-spike-dim">
                  First time? Use the Sign Up tab to create your Spike Sports account in seconds.
                </p>
                {isStandalone && !bookingId && (
                  <button
                    type="button"
                    data-testid="checkin-guest-btn"
                    onClick={() => navigate("/checkin?guest=1")}
                    className="mt-4 w-full inline-flex items-center justify-center gap-2 border border-white/15 hover:border-spike-blue text-spike-text-muted hover:text-spike-text font-orbitron text-[0.65rem] uppercase tracking-[0.2em] px-6 py-3 transition-colors"
                  >
                    Continue as guest
                  </button>
                )}
              </div>
            )}

            {step === STEP.GUEST_INFO && (
              <div className="bg-spike-elevated border border-white/10 p-8" data-testid="checkin-step-guest-info">
                <ShieldCheck className="w-8 h-8 text-spike-blue-light mb-5" strokeWidth={1.5} />
                <h2 className="font-orbitron font-bold uppercase text-xl text-spike-text tracking-wider">
                  Guest details
                </h2>
                <p className="mt-3 font-exo text-spike-text-muted text-sm">
                  No account needed. We&apos;ll email your signed waiver and personal check-in QR.
                </p>
                <div className="mt-6 space-y-4">
                  <div>
                    <label className="block text-[0.6rem] font-bold uppercase tracking-[0.25em] text-spike-text-muted mb-2 font-orbitron">
                      Full name
                    </label>
                    <input
                      type="text"
                      data-testid="guest-full-name"
                      value={guestFullName}
                      onChange={(e) => setGuestFullName(e.target.value)}
                      className="w-full bg-spike-elevated border border-white/10 px-4 py-3 text-spike-text font-exo"
                    />
                  </div>
                  <div>
                    <label className="block text-[0.6rem] font-bold uppercase tracking-[0.25em] text-spike-text-muted mb-2 font-orbitron">
                      Email
                    </label>
                    <input
                      type="email"
                      data-testid="guest-email"
                      value={guestEmail}
                      onChange={(e) => setGuestEmail(e.target.value)}
                      className="w-full bg-spike-elevated border border-white/10 px-4 py-3 text-spike-text font-exo"
                    />
                  </div>
                    <div>
                      <label className="block text-[0.6rem] font-bold uppercase tracking-[0.25em] text-spike-text-muted mb-2 font-orbitron">
                        Phone
                      </label>
                      <PhoneInput
                        dialCode={guestPhoneDial}
                        nationalNumber={guestPhoneNational}
                        onDialCodeChange={setGuestPhoneDial}
                        onNationalNumberChange={setGuestPhoneNational}
                        placeholder="647 555 0100"
                        data-testid="guest-phone"
                      />
                    </div>
                  <button
                    type="button"
                    data-testid="guest-continue-waiver-btn"
                    onClick={() => {
                      if (guestFullName.trim().length < 2) {
                        toast.error("Please enter your full name.");
                        return;
                      }
                      if (!guestEmail.trim()) {
                        toast.error("Please enter your email.");
                        return;
                      }
                      if (!isValidPhoneForApi(guestPhoneDial, guestPhoneNational)) {
                        toast.error("Please enter a valid phone number.");
                        return;
                      }
                      setSignatureName(guestFullName.trim());
                      setStep(STEP.WAIVER);
                    }}
                    className="w-full inline-flex items-center justify-center gap-3 bg-spike-blue hover:bg-spike-blue-hover text-white font-orbitron font-bold uppercase tracking-[0.22em] text-sm px-6 py-4"
                  >
                    Continue to waiver <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}

            {step === STEP.WAIVER && (
              <div className="bg-spike-elevated border border-white/10 p-8" data-testid="checkin-step-waiver">
                <ShieldCheck className="w-7 h-7 text-spike-blue-light mb-5" strokeWidth={1.5} />
                <h2 className="font-orbitron font-bold uppercase text-xl text-spike-text tracking-wider">
                  Liability waiver
                </h2>
                <p className="mt-3 font-exo text-spike-text-muted text-sm">
                  Please read and sign. You only need to do this once — it'll be on file for all future visits.
                </p>

                <div
                  data-testid="waiver-text"
                  className="mt-5 max-h-[280px] overflow-y-auto bg-spike-elevated border border-white/10 p-5 font-exo text-sm text-spike-text whitespace-pre-line leading-relaxed"
                >
                  {waiver?.text}
                </div>

                <div className="mt-6 space-y-4">
                  <label className="flex items-start gap-3 cursor-pointer" data-testid="waiver-agree-label">
                    <input
                      type="checkbox"
                      checked={agreed}
                      onChange={(e) => setAgreed(e.target.checked)}
                      data-testid="waiver-agree-checkbox"
                      className="mt-1 w-4 h-4 accent-spike-blue"
                    />
                    <span className="font-exo text-sm text-spike-text">
                      I have read and agree to the Spike Sports liability waiver.
                    </span>
                  </label>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      {signatureType === "typed" ? (
                        <label className="flex items-start gap-3 cursor-pointer mb-4" data-testid="waiver-typed-intent-label">
                          <input
                            type="checkbox"
                            checked={typedSignIntent}
                            onChange={(e) => setTypedSignIntent(e.target.checked)}
                            data-testid="waiver-typed-intent-checkbox"
                            className="mt-1 w-4 h-4 accent-spike-blue"
                          />
                          <span className="font-exo text-sm text-spike-text">
                            I check this box and type my name below to confirm I intend to legally sign and be bound by this waiver.
                          </span>
                        </label>
                      ) : null}
                      <label className="block text-[0.6rem] font-bold uppercase tracking-[0.25em] text-spike-text-muted mb-2 font-orbitron">
                        Participant name (signature)
                      </label>
                      <input
                        type="text"
                        data-testid="waiver-signature-input"
                        value={signatureName}
                        onChange={(e) => setSignatureName(e.target.value)}
                        placeholder="Your full legal name"
                        className="w-full bg-spike-elevated border border-white/10 px-4 py-3 text-spike-text placeholder:text-spike-dim focus:border-spike-blue focus:outline-none font-exo italic"
                      />
                      <div className="mt-3 flex items-center gap-2" data-testid="waiver-signature-type-toggle">
                        <button
                          type="button"
                          onClick={() => setSignatureType("typed")}
                          data-testid="waiver-sig-typed-btn"
                          className={`text-[0.6rem] font-bold uppercase tracking-[0.2em] font-orbitron px-3 py-1.5 rounded-sm border transition-colors ${signatureType === "typed" ? "bg-spike-blue text-black border-spike-blue" : "text-spike-text-muted border-white/15 hover:border-white/30"}`}
                        >
                          Typed
                        </button>
                        <button
                          type="button"
                          onClick={() => setSignatureType("drawn")}
                          data-testid="waiver-sig-drawn-btn"
                          className={`text-[0.6rem] font-bold uppercase tracking-[0.2em] font-orbitron px-3 py-1.5 rounded-sm border transition-colors ${signatureType === "drawn" ? "bg-spike-blue text-black border-spike-blue" : "text-spike-text-muted border-white/15 hover:border-white/30"}`}
                        >
                          Drawn
                        </button>
                      </div>
                      {signatureType === "drawn" ? (
                        <div className="mt-3">
                          <SignaturePad
                            value={signatureDrawnPng}
                            onChange={setSignatureDrawnPng}
                            data-testid="waiver-signature-pad"
                          />
                        </div>
                      ) : null}
                    </div>
                    <div>
                      <label className="block text-[0.6rem] font-bold uppercase tracking-[0.25em] text-spike-text-muted mb-2 font-orbitron">
                        Date of birth
                      </label>
                      <DateOfBirthPicker
                        value={dob}
                        onChange={setDob}
                        data-testid="waiver-dob"
                      />
                      {ageYears !== null && ageYears >= 5 && ageYears <= 120 && (
                        <div className="mt-2 text-[0.65rem] font-orbitron uppercase tracking-[0.2em] text-spike-blue-light" data-testid="waiver-age-readout">
                          {ageYears} yrs {isMinor && "· minor — guardian required"}
                        </div>
                      )}
                    </div>
                  </div>

                  {isMinor && (
                    <div data-testid="waiver-guardian-block" className="border border-amber-500/30 bg-amber-500/5 p-4 space-y-4">
                      <p className="text-[0.65rem] font-orbitron uppercase tracking-[0.2em] text-amber-200">
                        Legal guardian (required — participant is under 18)
                      </p>
                      <div>
                        <label className="block text-[0.6rem] font-bold uppercase tracking-[0.25em] text-amber-200 mb-2 font-orbitron">
                          Guardian full name
                        </label>
                        <input
                          type="text"
                          data-testid="waiver-guardian-input"
                          value={guardianName}
                          onChange={(e) => setGuardianName(e.target.value)}
                          placeholder="Parent or guardian's full name"
                          className="w-full bg-spike-elevated border border-white/10 px-4 py-3 text-spike-text placeholder:text-spike-dim focus:border-spike-blue focus:outline-none font-exo"
                        />
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-[0.6rem] font-bold uppercase tracking-[0.25em] text-amber-200 mb-2 font-orbitron">
                            Guardian email
                          </label>
                          <input
                            type="email"
                            data-testid="waiver-guardian-email"
                            value={guardianEmail}
                            onChange={(e) => setGuardianEmail(e.target.value)}
                            placeholder="guardian@email.com"
                            className="w-full bg-spike-elevated border border-white/10 px-4 py-3 text-spike-text placeholder:text-spike-dim focus:border-spike-blue focus:outline-none font-exo"
                          />
                        </div>
                        <div>
                          <label className="block text-[0.6rem] font-bold uppercase tracking-[0.25em] text-amber-200 mb-2 font-orbitron">
                            Guardian phone
                          </label>
                          <PhoneInput
                            dialCode={guardianPhoneDial}
                            nationalNumber={guardianPhoneNational}
                            onDialCodeChange={setGuardianPhoneDial}
                            onNationalNumberChange={setGuardianPhoneNational}
                            placeholder="647 555 0100"
                            data-testid="waiver-guardian-phone"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-[0.6rem] font-bold uppercase tracking-[0.25em] text-amber-200 mb-2 font-orbitron">
                          Relationship to participant
                        </label>
                        <input
                          type="text"
                          data-testid="waiver-guardian-relationship"
                          value={guardianRelationship}
                          onChange={(e) => setGuardianRelationship(e.target.value)}
                          placeholder="e.g. Mother, Father, Legal guardian"
                          className="w-full bg-spike-elevated border border-white/10 px-4 py-3 text-spike-text placeholder:text-spike-dim focus:border-spike-blue focus:outline-none font-exo"
                        />
                      </div>
                      <p className="text-[0.65rem] text-amber-200/80 font-exo">
                        By providing these details, you confirm you are the parent or legal guardian
                        of {signatureName || "the participant"} and consent on their behalf.
                      </p>
                    </div>
                  )}

                  <button
                    type="button"
                    data-testid="waiver-sign-btn"
                    disabled={submitting}
                    onClick={signWaiver}
                    className="w-full inline-flex items-center justify-center gap-3 bg-spike-blue hover:bg-spike-blue-hover disabled:opacity-50 text-white font-orbitron font-bold uppercase tracking-[0.22em] text-sm px-6 py-4 transition-colors"
                  >
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Sign & continue <ArrowRight className="w-4 h-4" /></>}
                  </button>
                </div>
              </div>
            )}

            {step === STEP.CONFIRM && (
              <div className="bg-spike-elevated border border-white/10 p-8" data-testid="checkin-step-confirm">
                <CheckCircle2 className="w-8 h-8 text-spike-blue-light mb-5" strokeWidth={1.5} />
                <h2 className="font-orbitron font-bold uppercase text-xl text-spike-text tracking-wider">
                  Confirm your check-in
                </h2>
                <p className="mt-3 font-exo text-spike-text-muted text-sm">
                  Signed in as <span className="text-spike-text">{user?.email}</span>.
                  {waiverStatus?.signed && " Waiver on file"}
                  {waiverStatus?.signed_at && ` since ${new Date(waiverStatus.signed_at).toLocaleDateString()}`}.
                  {isStandalone
                    ? " Tap below to mark yourself in."
                    : " Tap below and you're on the lane."}
                </p>
                <button
                  type="button"
                  data-testid="checkin-confirm-btn"
                  disabled={submitting}
                  onClick={confirmCheckIn}
                  className="mt-6 w-full inline-flex items-center justify-center gap-3 bg-spike-blue hover:bg-spike-blue-hover disabled:opacity-50 text-white font-orbitron font-bold uppercase tracking-[0.22em] text-sm px-6 py-4 transition-colors"
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Check me in <CheckCircle2 className="w-4 h-4" /></>}
                </button>
              </div>
            )}

            {step === STEP.DONE && (
              <div className="bg-spike-elevated border border-spike-blue/40 p-8 text-center" data-testid="checkin-step-done">
                <div className="w-16 h-16 mx-auto bg-spike-blue/15 border border-spike-blue/50 flex items-center justify-center mb-5">
                  <CheckCircle2 className="w-9 h-9 text-spike-blue-light" strokeWidth={2} />
                </div>
                <h2 className="font-orbitron font-black uppercase text-2xl text-spike-text tracking-tight">
                  You're on the lane.
                </h2>
                <p className="mt-3 font-exo text-spike-text-muted text-sm">
                  Welcome, <span className="text-spike-text">{user?.name || user?.email}</span>.
                  Show this screen at the front desk if asked.
                </p>
                <div className="mt-6 inline-block bg-spike-elevated border border-spike-blue px-6 py-3 font-orbitron text-spike-blue-light tracking-[0.3em] text-sm">
                  ✓ CHECKED IN
                  {myCheckIn?.checked_in_at && (
                    <span className="block mt-1 text-[0.6rem] text-spike-blue-light tracking-[0.2em]">
                      {new Date(myCheckIn.checked_in_at).toLocaleString()}
                    </span>
                  )}
                </div>
                <div className="mt-8 flex flex-wrap gap-3 justify-center">
                  <button
                    type="button"
                    onClick={() => navigate("/account")}
                    className="inline-flex items-center gap-2 bg-transparent text-spike-text border border-white/10 hover:border-spike-blue hover:bg-spike-blue/10 font-orbitron text-[0.7rem] uppercase tracking-[0.25em] px-5 py-3 transition-colors"
                  >
                    My account
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate("/")}
                    className="inline-flex items-center gap-2 bg-transparent text-spike-text-muted hover:text-spike-text font-orbitron text-[0.7rem] uppercase tracking-[0.25em] px-5 py-3"
                  >
                    Home
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default CheckIn;
